/*!
 * password-crypto.js — shared primitives for Timbrica password & secrets tools.
 *
 * Public surface (attached to window.PwdCrypto):
 *   - randomInt(maxExclusive)        crypto.getRandomValues-backed unbiased int in [0, max)
 *   - randomBytes(n)                 Uint8Array of n random bytes
 *   - randomFromAlphabet(alphabet, n) unbiased pick of n chars
 *   - shuffle(arr)                   Fisher–Yates in-place using crypto
 *   - calcEntropyBits(len, poolSize) Shannon entropy lower bound
 *   - estimateCrackSeconds(bits, hashesPerSecond) seconds to brute-force
 *   - formatDuration(seconds)        humanised "in 3.2 years" etc, locale-aware
 *   - sha1Hex(text)                  SHA-1 hex (used for HIBP k-anonymity)
 *   - sha256Bytes(text)              Uint8Array
 *   - aesGcmEncrypt(plaintext, password) {ciphertext, salt, iv} base64
 *   - aesGcmDecrypt(payload, password) plaintext
 *   - bytesToBase64Url / base64UrlToBytes
 *   - bytesToBase32 / base32ToBytes  RFC4648 for TOTP secrets
 *   - hotp(secretBytes, counter, digits) RFC4226
 *   - totp(secretBytes, t, period, digits) RFC6238
 *   - zxcvbnLoad()                   lazy-load zxcvbn ESM, returns the global zxcvbn fn
 *
 * Design constraints:
 *   - No third-party network calls from this module itself (HIBP is in the breach-checker blade).
 *   - All randomness via crypto.getRandomValues (Math.random NEVER used).
 *   - Works in service-worker context for tools that pre-cache.
 */
(function (global) {
    'use strict';

    if (global.PwdCrypto) return;

    const subtle = (global.crypto && global.crypto.subtle) || null;

    /* ── Randomness ──────────────────────────────────────────────────── */
    function randomBytes(n) {
        const out = new Uint8Array(n);
        global.crypto.getRandomValues(out);
        return out;
    }

    // Unbiased random integer in [0, max). Rejects results above the largest
    // multiple of `max` that fits in a uint32 to avoid modulo bias.
    function randomInt(max) {
        if (max <= 0 || max > 0xffffffff) throw new RangeError('max out of range');
        const limit = Math.floor(0x100000000 / max) * max;
        const buf = new Uint32Array(1);
        let x;
        do {
            global.crypto.getRandomValues(buf);
            x = buf[0];
        } while (x >= limit);
        return x % max;
    }

    function randomFromAlphabet(alphabet, n) {
        if (!alphabet || alphabet.length === 0) return '';
        // Convert to array of code points to handle multi-codepoint chars safely.
        const chars = Array.from(alphabet);
        let out = '';
        for (let i = 0; i < n; i++) out += chars[randomInt(chars.length)];
        return out;
    }

    function shuffle(arr) {
        for (let i = arr.length - 1; i > 0; i--) {
            const j = randomInt(i + 1);
            const tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
        }
        return arr;
    }

    /* ── Entropy & crack time ────────────────────────────────────────── */
    function calcEntropyBits(length, poolSize) {
        if (length <= 0 || poolSize <= 1) return 0;
        return length * Math.log2(poolSize);
    }

    // Default rate: modern GPU rig hashing ≈ 10^11 H/s for fast hashes (MD5/SHA1).
    // Conservative for the worst case the user faces (a determined offline attacker).
    function estimateCrackSeconds(bits, hashesPerSecond = 1e11) {
        // Half the keyspace on average.
        const guesses = Math.pow(2, bits) / 2;
        return guesses / hashesPerSecond;
    }

    function formatDuration(seconds, locale) {
        const L = (locale || (global.__LOCALE) || 'en');
        const I18N = {
            instant:    { en: 'instantly', ru: 'мгновенно', ko: '즉시', ar: 'فوراً', id: 'seketika', pt: 'instantaneamente', de: 'sofort', es: 'al instante', fr: 'instantanément', uk: 'миттєво', tr: 'anında' },
            seconds:    { en: 'seconds', ru: 'секунд', ko: '초', ar: 'ثواني', id: 'detik', pt: 'segundos', de: 'Sekunden', es: 'segundos', fr: 'secondes', uk: 'секунд', tr: 'saniye' },
            minutes:    { en: 'minutes', ru: 'минут', ko: '분', ar: 'دقائق', id: 'menit', pt: 'minutos', de: 'Minuten', es: 'minutos', fr: 'minutes', uk: 'хвилин', tr: 'dakika' },
            hours:      { en: 'hours', ru: 'часов', ko: '시간', ar: 'ساعات', id: 'jam', pt: 'horas', de: 'Stunden', es: 'horas', fr: 'heures', uk: 'годин', tr: 'saat' },
            days:       { en: 'days', ru: 'дней', ko: '일', ar: 'أيام', id: 'hari', pt: 'dias', de: 'Tage', es: 'días', fr: 'jours', uk: 'днів', tr: 'gün' },
            years:      { en: 'years', ru: 'лет', ko: '년', ar: 'سنوات', id: 'tahun', pt: 'anos', de: 'Jahre', es: 'años', fr: 'ans', uk: 'років', tr: 'yıl' },
            centuries:  { en: 'centuries', ru: 'веков', ko: '세기', ar: 'قرون', id: 'abad', pt: 'séculos', de: 'Jahrhunderte', es: 'siglos', fr: 'siècles', uk: 'століть', tr: 'yüzyıl' },
            eternity:   { en: 'longer than the universe', ru: 'дольше существования вселенной', ko: '우주의 나이보다 길다', ar: 'أطول من عمر الكون', id: 'lebih lama dari usia alam semesta', pt: 'mais que a idade do universo', de: 'länger als das Universum', es: 'más que la edad del universo', fr: 'plus que l’âge de l’univers', uk: 'довше існування всесвіту', tr: 'evrenin yaşından daha uzun' },
        };
        const t = (k) => (I18N[k] && I18N[k][L]) || I18N[k].en;
        if (!isFinite(seconds) || seconds < 0) return '—';
        if (seconds < 1) return t('instant');
        if (seconds < 60) return Math.round(seconds) + ' ' + t('seconds');
        if (seconds < 3600) return Math.round(seconds / 60) + ' ' + t('minutes');
        if (seconds < 86400) return Math.round(seconds / 3600) + ' ' + t('hours');
        if (seconds < 86400 * 365) return Math.round(seconds / 86400) + ' ' + t('days');
        const years = seconds / (86400 * 365.25);
        if (years < 1e2) return Math.round(years) + ' ' + t('years');
        if (years < 1e6) return (years / 100).toFixed(0) + ' ' + t('centuries');
        if (years < 1e12) {
            const exp = Math.floor(Math.log10(years));
            return `10^${exp} ` + t('years');
        }
        return t('eternity');
    }

    /* ── Hashing ─────────────────────────────────────────────────────── */
    async function sha1Hex(text) {
        const buf = new TextEncoder().encode(text);
        const out = await subtle.digest('SHA-1', buf);
        const arr = new Uint8Array(out);
        let hex = '';
        for (const b of arr) hex += b.toString(16).padStart(2, '0');
        return hex.toUpperCase();
    }
    async function sha256Bytes(text) {
        const buf = new TextEncoder().encode(text);
        return new Uint8Array(await subtle.digest('SHA-256', buf));
    }

    /* ── AES-GCM (password-derived key, PBKDF2 600k) ─────────────────── */
    async function deriveKey(password, salt, iterations = 600000) {
        const baseKey = await subtle.importKey(
            'raw', new TextEncoder().encode(password),
            'PBKDF2', false, ['deriveKey']
        );
        return subtle.deriveKey(
            { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
            baseKey,
            { name: 'AES-GCM', length: 256 },
            false, ['encrypt', 'decrypt']
        );
    }

    async function aesGcmEncrypt(plaintext, password) {
        const salt = randomBytes(16);
        const iv = randomBytes(12);
        const key = await deriveKey(password, salt);
        const ct = new Uint8Array(await subtle.encrypt(
            { name: 'AES-GCM', iv }, key, new TextEncoder().encode(plaintext)
        ));
        return {
            ciphertext: bytesToBase64Url(ct),
            salt: bytesToBase64Url(salt),
            iv: bytesToBase64Url(iv),
        };
    }
    async function aesGcmDecrypt(payload, password) {
        const salt = base64UrlToBytes(payload.salt);
        const iv = base64UrlToBytes(payload.iv);
        const ct = base64UrlToBytes(payload.ciphertext);
        const key = await deriveKey(password, salt);
        const pt = await subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
        return new TextDecoder().decode(pt);
    }

    /* ── Encoding ────────────────────────────────────────────────────── */
    function bytesToBase64Url(bytes) {
        let bin = '';
        for (const b of bytes) bin += String.fromCharCode(b);
        return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    function base64UrlToBytes(str) {
        const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4));
        const bin = atob(str.replace(/-/g, '+').replace(/_/g, '/') + pad);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
    }

    const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    function bytesToBase32(bytes) {
        let bits = 0, value = 0, out = '';
        for (const b of bytes) {
            value = (value << 8) | b; bits += 8;
            while (bits >= 5) {
                out += BASE32_ALPHABET[(value >>> (bits - 5)) & 31];
                bits -= 5;
            }
        }
        if (bits > 0) out += BASE32_ALPHABET[(value << (5 - bits)) & 31];
        return out;
    }
    function base32ToBytes(str) {
        const cleaned = str.toUpperCase().replace(/=+$/, '').replace(/\s+/g, '');
        const out = [];
        let bits = 0, value = 0;
        for (const ch of cleaned) {
            const idx = BASE32_ALPHABET.indexOf(ch);
            if (idx < 0) throw new Error('invalid base32 character: ' + ch);
            value = (value << 5) | idx; bits += 5;
            if (bits >= 8) { out.push((value >>> (bits - 8)) & 0xff); bits -= 8; }
        }
        return new Uint8Array(out);
    }

    /* ── HOTP / TOTP (RFC 4226 / 6238) ───────────────────────────────── */
    async function hmacSha1(keyBytes, msgBytes) {
        const key = await subtle.importKey('raw', keyBytes, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
        const sig = await subtle.sign('HMAC', key, msgBytes);
        return new Uint8Array(sig);
    }
    async function hotp(secretBytes, counter, digits = 6) {
        const buf = new ArrayBuffer(8);
        const dv = new DataView(buf);
        dv.setUint32(0, Math.floor(counter / 0x100000000));
        dv.setUint32(4, counter >>> 0);
        const hmac = await hmacSha1(secretBytes, new Uint8Array(buf));
        const off = hmac[hmac.length - 1] & 0x0f;
        const code = ((hmac[off] & 0x7f) << 24)
                   | ((hmac[off + 1] & 0xff) << 16)
                   | ((hmac[off + 2] & 0xff) << 8)
                   | (hmac[off + 3] & 0xff);
        return String(code % Math.pow(10, digits)).padStart(digits, '0');
    }
    async function totp(secretBytes, t = Date.now() / 1000, period = 30, digits = 6) {
        return hotp(secretBytes, Math.floor(t / period), digits);
    }

    /* ── Shamir Secret Sharing over GF(2^8) ──────────────────────────────
       Splits a secret into N shares so any K reconstruct it and fewer than K
       reveal nothing (information-theoretic security).

       GENERATOR MUST BE 3. The field is GF(2^8) with the AES reduction
       polynomial 0x11b, and 2 (0x02) is NOT a primitive element there — it
       cycles through only 51 of the 255 non-zero elements, leaving the log/exp
       tables full of holes and collisions and silently corrupting every
       split/combine. (This was the root cause of the long-standing
       "combine returns different text" bug, fixed 2026-06-14.) 3 is primitive.

       Each share is base64url of a self-describing container:
         [0]    MAGIC 0x53 ('S')  — base64 of 0x53 always starts 'U', so a
                                    share is trivially recognisable in text.
         [1]    VERSION 1
         [2]    k  (threshold)    — lets combine detect "not enough shares".
         [3]    x  (index, 1..255)
         [4..6] setId (3 random bytes, equal across one split) — lets combine
                detect shares accidentally mixed from DIFFERENT splits.
         [7..]  y  (GF share of [secret || 4-byte SHA-256 digest of secret]).
       The digest is appended to the secret BEFORE splitting, so it is itself
       secret-shared (never leaks) yet lets combine VERIFY the reconstruction
       instead of returning silent garbage. */
    const _sssGF = (() => {
        const exp = new Uint8Array(512), log = new Uint8Array(256);
        let x = 1;
        for (let i = 0; i < 255; i++) {
            exp[i] = x; log[x] = i;
            let x2 = x << 1; if (x2 & 0x100) x2 ^= 0x11b; // xtime (×2)
            x = x2 ^ x;                                    // ×3 — generator 3
        }
        for (let i = 255; i < 512; i++) exp[i] = exp[i - 255];
        return {
            mul: (a, b) => (a === 0 || b === 0) ? 0 : exp[log[a] + log[b]],
            div: (a, b) => { if (a === 0) return 0; if (b === 0) throw new Error('div0'); return exp[(log[a] - log[b] + 255) % 255]; },
        };
    })();
    function _sssEval(coeffs, x) { let r = 0; for (let i = coeffs.length - 1; i >= 0; i--) r = _sssGF.mul(r, x) ^ coeffs[i]; return r; }
    function _sssSplitBytes(payload, n, k) {
        const out = [];
        for (let i = 1; i <= n; i++) out.push({ x: i, y: new Uint8Array(payload.length) });
        for (let i = 0; i < payload.length; i++) {
            const coeffs = new Uint8Array(k);
            coeffs[0] = payload[i];
            const rand = randomBytes(k - 1);
            for (let j = 0; j < k - 1; j++) coeffs[j + 1] = rand[j];
            for (let s = 0; s < n; s++) out[s].y[i] = _sssEval(coeffs, out[s].x);
        }
        return out;
    }
    function _sssCombineBytes(shares) { // Lagrange interpolation at x=0
        const len = shares[0].y.length, out = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            let acc = 0;
            for (let j = 0; j < shares.length; j++) {
                let num = 1, den = 1;
                for (let m = 0; m < shares.length; m++) {
                    if (m === j) continue;
                    num = _sssGF.mul(num, shares[m].x);            // (0 - x_m) = x_m in GF(2^8)
                    den = _sssGF.mul(den, shares[j].x ^ shares[m].x);
                }
                acc ^= _sssGF.mul(shares[j].y[i], _sssGF.div(num, den));
            }
            out[i] = acc;
        }
        return out;
    }
    const SSS_MAGIC = 0x53, SSS_VER = 1, SSS_DIGEST = 4;
    const SSS_ERR = { BAD: 'bad_format', LEGACY: 'legacy_unsupported', MIXED: 'mixed_set', FEW: 'insufficient', INTEG: 'integrity', LEN: 'length_mismatch' };
    async function _sssDigest(bytes) { return new Uint8Array(await subtle.digest('SHA-256', bytes)).slice(0, SSS_DIGEST); }
    function _hex2(b) { return b.toString(16).padStart(2, '0').toUpperCase(); }

    // Split secretBytes (Uint8Array) into n shares, any k of which recover it.
    // Returns { lines: string[], setCode: string }.
    async function sssSplit(secretBytes, n, k) {
        if (!(n >= 2 && n <= 255 && k >= 2 && k <= n)) throw new RangeError('bad (n,k)');
        const digest = await _sssDigest(secretBytes);
        const payload = new Uint8Array(secretBytes.length + SSS_DIGEST);
        payload.set(secretBytes, 0); payload.set(digest, secretBytes.length);
        const setId = randomBytes(3);
        const lines = _sssSplitBytes(payload, n, k).map(sh => {
            const c = new Uint8Array(7 + sh.y.length);
            c[0] = SSS_MAGIC; c[1] = SSS_VER; c[2] = k; c[3] = sh.x;
            c.set(setId, 4); c.set(sh.y, 7);
            return bytesToBase64Url(c);
        });
        return { lines, setCode: _hex2(setId[0]) + _hex2(setId[1]) + _hex2(setId[2]) };
    }
    // Strict validation of one share token → parsed share, { legacy:true }, or null.
    function sssParse(token) {
        token = String(token).trim();
        if (/^\d{1,3}-[A-Za-z0-9_-]+$/.test(token)) return { legacy: true }; // old broken-table format
        let c;
        try { c = base64UrlToBytes(token); } catch { return null; }
        if (c.length < 8 || c[0] !== SSS_MAGIC || c[1] !== SSS_VER) return null;
        const k = c[2], x = c[3];
        if (k < 2 || x < 1 || x > 255) return null;
        return { k, x, setId: c[4] + ',' + c[5] + ',' + c[6], y: c.slice(7) };
    }
    // Pull share tokens out of arbitrary pasted text — robust to any separator
    // (newlines, spaces, commas) and to labels/quotes a user may copy by
    // accident (e.g. "#1: Uw…", "1) Uw…"). Modern shares always begin 'U'.
    function sssExtractTokens(text) {
        return String(text).match(/\d{1,3}-[A-Za-z0-9_-]{6,}|U[A-Za-z0-9_-]{14,}/g) || [];
    }
    // Combine tokens → { secret: Uint8Array } or { err, ... } (codes in SSS_ERR).
    async function sssCombine(tokens) {
        const parsed = [];
        for (const t of tokens) {
            const p = sssParse(t);
            if (p && p.legacy) return { err: SSS_ERR.LEGACY };
            if (!p) return { err: SSS_ERR.BAD };
            parsed.push(p);
        }
        if (!parsed.length) return { err: SSS_ERR.BAD };
        if (new Set(parsed.map(p => p.setId)).size > 1) return { err: SSS_ERR.MIXED };
        if (new Set(parsed.map(p => p.k)).size > 1) return { err: SSS_ERR.MIXED };
        const k = parsed[0].k;
        const seen = new Set(), uniq = [];
        for (const p of parsed) { if (seen.has(p.x)) continue; seen.add(p.x); uniq.push(p); } // dedupe by x
        if (uniq.length < k) return { err: SSS_ERR.FEW, need: k, have: uniq.length };
        const len = uniq[0].y.length;
        if (uniq.some(p => p.y.length !== len)) return { err: SSS_ERR.LEN };
        const payload = _sssCombineBytes(uniq);
        if (payload.length < SSS_DIGEST) return { err: SSS_ERR.INTEG };
        const secret = payload.slice(0, payload.length - SSS_DIGEST);
        const tag = payload.slice(payload.length - SSS_DIGEST);
        const want = await _sssDigest(secret);
        for (let i = 0; i < SSS_DIGEST; i++) if (tag[i] !== want[i]) return { err: SSS_ERR.INTEG };
        return { secret };
    }

    /* ── Vendor-script loader: our own copy first (17.10.2026) ───────────
       URLs come from window.__pwdCdn[name] — cdn_chain() on the server: own
       copy → own second address → external mirror last. Until 17.10.2026 the
       blades built [cdn('jsdelivr', …), cdn_fallback(…), /vendor/…]: intl
       loaded zxcvbn / kdbxweb / openpgp / jsQR from jsdelivr FIRST and ru from
       Beget's proxy, the own copy (byte-identical, on both origins) came last,
       after two failures. The chain is walked by the shared
       window.__vendorScript (bounded attempts, «200 without the library» is a
       failure, asset_fallback telemetry); the loop below is only for a page
       where timbrica-core.js did not arrive. */
    const EXPECT = { zxcvbn: 'zxcvbn', kdbxweb: 'kdbxweb', openpgp: 'openpgp', jsqr: 'jsQR' };
    // Copy nonce from an existing trusted script so the loaded vendor passes
    // CSP `strict-dynamic`. Required by Chromium when nonce-based CSP is in
    // effect — without it, dynamically appended <script> is blocked even
    // though it was created by an already-nonce'd script.
    function _scriptNonce() {
        const own = document.currentScript && document.currentScript.nonce;
        if (own) return own;
        const any = document.querySelector('script[nonce]');
        return any ? (any.nonce || any.getAttribute('nonce') || '') : '';
    }

    const _loaderPromises = Object.create(null);
    function loadScriptWithFallback(name, fallbackUrl) {
        if (_loaderPromises[name]) return _loaderPromises[name];
        const list = (global.__pwdCdn && Array.isArray(global.__pwdCdn[name]) ? global.__pwdCdn[name] : null)
            || (fallbackUrl ? [fallbackUrl] : []);
        if (!list.length) return Promise.reject(new Error('no urls for ' + name));
        if (typeof global.__vendorScript === 'function') {
            _loaderPromises[name] = global.__vendorScript(list, { expect: EXPECT[name], label: name })
                .then(() => undefined, (e) => { delete _loaderPromises[name]; throw new Error(name + ' all sources failed'); });
            return _loaderPromises[name];
        }
        const nonce = _scriptNonce();
        _loaderPromises[name] = new Promise((resolve, reject) => {
            let i = 0;
            (function tryNext() {
                if (i >= list.length) { delete _loaderPromises[name]; return reject(new Error(name + ' all sources failed')); }
                const s = document.createElement('script');
                s.src = list[i++];
                s.async = true;
                s.crossOrigin = 'anonymous';
                if (nonce) s.setAttribute('nonce', nonce);
                s.onload = () => resolve();
                s.onerror = tryNext;
                document.head.appendChild(s);
            })();
        });
        return _loaderPromises[name];
    }

    /* ── zxcvbn lazy loader ── */
    function zxcvbnLoad() {
        if (global.zxcvbn) return Promise.resolve(global.zxcvbn);
        return loadScriptWithFallback('zxcvbn', '/vendor/zxcvbn/zxcvbn.js')
            .then(() => global.zxcvbn);
    }

    /* ── Charset builders ─────────────────────────────────────────────── */
    const CHARSETS = {
        lower:   'abcdefghijklmnopqrstuvwxyz',
        upper:   'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
        digits:  '0123456789',
        symbols: '!@#$%^&*()-_=+[]{};:,.<>?/~',
        space:   ' ',
        // Characters that look alike in common fonts; subtracted when "exclude similar" is on.
        similar:   'l1IO0o',
        // Characters that often break copy-paste or shell scripts.
        ambiguous: '{}[]()/\\\'"`~,;.<>',
    };
    function buildAlphabet(opts) {
        let alpha = '';
        if (opts.lower) alpha += CHARSETS.lower;
        if (opts.upper) alpha += CHARSETS.upper;
        if (opts.digits) alpha += CHARSETS.digits;
        if (opts.symbols) alpha += CHARSETS.symbols;
        if (opts.space) alpha += CHARSETS.space;
        if (opts.extra) alpha += String(opts.extra);
        // De-dupe
        alpha = Array.from(new Set(alpha)).join('');
        if (opts.excludeSimilar) {
            const skip = new Set(CHARSETS.similar);
            alpha = Array.from(alpha).filter(c => !skip.has(c)).join('');
        }
        if (opts.excludeAmbiguous) {
            const skip = new Set(CHARSETS.ambiguous);
            alpha = Array.from(alpha).filter(c => !skip.has(c)).join('');
        }
        return alpha;
    }

    // Generate ensuring at least one char from each enabled class (NIST 800-63B note: not required,
    // but most password policies still demand it). Skip enforcement if length < requiredClasses.
    function generatePassword(length, opts) {
        const alpha = buildAlphabet(opts);
        if (!alpha.length) return '';
        if (!opts.requireAll || length < 4) return randomFromAlphabet(alpha, length);
        const classes = [];
        if (opts.lower)   classes.push(buildAlphabet({ lower: 1, excludeSimilar: opts.excludeSimilar, excludeAmbiguous: opts.excludeAmbiguous }));
        if (opts.upper)   classes.push(buildAlphabet({ upper: 1, excludeSimilar: opts.excludeSimilar, excludeAmbiguous: opts.excludeAmbiguous }));
        if (opts.digits)  classes.push(buildAlphabet({ digits: 1, excludeSimilar: opts.excludeSimilar, excludeAmbiguous: opts.excludeAmbiguous }));
        if (opts.symbols) classes.push(buildAlphabet({ symbols: 1, excludeSimilar: opts.excludeSimilar, excludeAmbiguous: opts.excludeAmbiguous }));
        const out = classes.filter(c => c.length).map(c => randomFromAlphabet(c, 1));
        while (out.length < length) out.push(randomFromAlphabet(alpha, 1));
        return shuffle(out).join('');
    }

    /* ── Pronounceable (Koremutake-like syllables) ───────────────────── */
    const SYLLABLES = [
        'ba','be','bi','bo','bu','by','da','de','di','do','du','dy','fa','fe','fi','fo','fu','fy',
        'ga','ge','gi','go','gu','gy','ha','he','hi','ho','hu','hy','ja','je','ji','jo','ju','jy',
        'ka','ke','ki','ko','ku','ky','la','le','li','lo','lu','ly','ma','me','mi','mo','mu','my',
        'na','ne','ni','no','nu','ny','pa','pe','pi','po','pu','py','ra','re','ri','ro','ru','ry',
        'sa','se','si','so','su','sy','ta','te','ti','to','tu','ty','va','ve','vi','vo','vu','vy',
        'tra','tre','tri','tro','tru','sta','ste','sti','sto','stu','sha','she','shi','sho','shu',
        'cha','che','chi','cho','chu','tha','the','thi','tho','thu','pha','phe','phi','pho','phu',
        'bra','bre','bri','bro','bru','gra','gre','gri','gro','gru','pra','pre','pri','pro','pru'
    ];
    function generatePronounceable(targetLength, opts) {
        let out = '';
        while (out.length < targetLength) {
            let s = SYLLABLES[randomInt(SYLLABLES.length)];
            if (opts && opts.titleCase && out.length === 0) s = s[0].toUpperCase() + s.slice(1);
            out += s;
        }
        out = out.slice(0, targetLength);
        if (opts && opts.appendDigits) {
            const n = opts.appendDigits;
            out = out.slice(0, Math.max(1, out.length - n)) + randomFromAlphabet(CHARSETS.digits, n);
        }
        return out;
    }

    /* ── Pattern syntax: c=lower, C=upper, d=digit, s=symbol, x=alphanum, *=any, \X=literal X ── */
    function generateFromPattern(pattern) {
        let out = '';
        for (let i = 0; i < pattern.length; i++) {
            const ch = pattern[i];
            if (ch === '\\' && i + 1 < pattern.length) { out += pattern[++i]; continue; }
            switch (ch) {
                case 'c': out += randomFromAlphabet(CHARSETS.lower, 1); break;
                case 'C': out += randomFromAlphabet(CHARSETS.upper, 1); break;
                case 'a': out += randomFromAlphabet(CHARSETS.lower + CHARSETS.upper, 1); break;
                case 'd': out += randomFromAlphabet(CHARSETS.digits, 1); break;
                case 's': out += randomFromAlphabet(CHARSETS.symbols, 1); break;
                case 'x': out += randomFromAlphabet(CHARSETS.lower + CHARSETS.upper + CHARSETS.digits, 1); break;
                case '*': out += randomFromAlphabet(CHARSETS.lower + CHARSETS.upper + CHARSETS.digits + CHARSETS.symbols, 1); break;
                default: out += ch;
            }
        }
        return out;
    }

    /* ── Diceware roll → wordlist index (5 dice → 1..7776) ───────────── */
    function rollDiceware(count = 5) {
        const dice = [];
        for (let i = 0; i < count; i++) dice.push(1 + randomInt(6));
        return dice;
    }
    function diceToIndex(dice) {
        let key = 0;
        for (const d of dice) key = key * 10 + d;
        return key; // e.g. 11111..66666 — caller looks it up in EFF list
    }

    /* ── Eye toggle for [data-pw-eye] password inputs ──────────────────
       Auto-runs on DOMContentLoaded and on dynamic insertion (mutation).
       Wraps `<input type=password data-pw-eye>` with a position:relative
       parent if it isn't one, and overlays a small ʘ/🙈 button that flips
       input.type. Idempotent — safe to call multiple times. */
    const _EYE_CSS_ID = '__pwd-eye-css';
    function _injectEyeCss() {
        if (document.getElementById(_EYE_CSS_ID)) return;
        const s = document.createElement('style');
        s.id = _EYE_CSS_ID;
        s.textContent = `
        .pwd-eye-host{position:relative;display:block}
        .pwd-eye-btn{position:absolute;inset-inline-end:8px;top:50%;transform:translateY(-50%);background:none;border:0;cursor:pointer;font-size:18px;line-height:1;padding:6px 8px;border-radius:6px;color:var(--text-muted,#94a3b8);transition:.15s;z-index:2;user-select:none}
        .pwd-eye-btn:hover{background:rgba(56,189,248,.08);color:var(--accent,#38bdf8)}
        .pwd-eye-btn:focus-visible{outline:2px solid var(--accent,#38bdf8);outline-offset:1px}
        .pwd-eye-host>input[data-pw-eye]{padding-inline-end:44px!important}
        `;
        (document.head || document.documentElement).appendChild(s);
    }
    function attachEyeToggle(input) {
        if (!input || input.dataset.pwEyeWired === '1') return;
        if (input.tagName !== 'INPUT') return;
        input.dataset.pwEyeWired = '1';
        _injectEyeCss();
        // Wrap in pwd-eye-host if the parent isn't already a single-input wrapper
        let host = input.parentElement;
        const onlyChild = host && host.children.length === 1 && host.firstElementChild === input;
        if (!onlyChild) {
            const wrap = document.createElement('span');
            wrap.className = 'pwd-eye-host';
            input.parentElement.insertBefore(wrap, input);
            wrap.appendChild(input);
            host = wrap;
        } else {
            host.classList.add('pwd-eye-host');
        }
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pwd-eye-btn';
        btn.setAttribute('aria-label', ((window.__commonStrings||{}).js_show_password || "Show password"));
        btn.setAttribute('tabindex', '0');
        btn.textContent = '👁';
        btn.addEventListener('click', () => {
            const now = input.type === 'password' ? 'text' : 'password';
            input.type = now;
            btn.textContent = now === 'password' ? '👁' : '🙈';
            btn.setAttribute('aria-label', now === 'password' ? ((window.__commonStrings||{}).js_show_password || "Show password") : 'Hide password');
        });
        host.appendChild(btn);
    }
    function _scanEyes(root) {
        (root || document).querySelectorAll('input[data-pw-eye]').forEach(attachEyeToggle);
    }
    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => _scanEyes());
        } else {
            _scanEyes();
        }
        // Catch inputs added dynamically (e.g. receive-password prompt appearing later)
        try {
            const mo = new MutationObserver(muts => {
                for (const m of muts) {
                    m.addedNodes.forEach(n => {
                        if (n.nodeType !== 1) return;
                        if (n.matches && n.matches('input[data-pw-eye]')) attachEyeToggle(n);
                        else _scanEyes(n);
                    });
                }
            });
            mo.observe(document.documentElement, { childList: true, subtree: true });
        } catch (_) { /* MutationObserver may be unavailable in very old browsers — page is already gated above */ }
    }

    /* ── Telemetry shorthand ────────────────────────────────────────────
       Wraps window.__ae() with the standard "feature_use" envelope so each
       tool only has to write PwdCrypto.ae(slug, 'action_name', { extra }).
       Quietly noop's if the analytics endpoint is unavailable. */
    function ae(slug, action, extra) {
        try {
            if (typeof global.__ae !== 'function') return;
            global.__ae('feature_use', Object.assign({ slug, action }, extra || {}));
        } catch (_) { /* swallow */ }
    }

    global.PwdCrypto = {
        // exports
        randomBytes, randomInt, randomFromAlphabet, shuffle,
        calcEntropyBits, estimateCrackSeconds, formatDuration,
        sha1Hex, sha256Bytes,
        aesGcmEncrypt, aesGcmDecrypt,
        bytesToBase64Url, base64UrlToBytes,
        bytesToBase32, base32ToBytes,
        hotp, totp,
        shamir: { split: sssSplit, parse: sssParse, extractTokens: sssExtractTokens, combine: sssCombine, ERR: SSS_ERR },
        zxcvbnLoad, loadScriptWithFallback,
        attachEyeToggle,
        ae,
        CHARSETS, buildAlphabet,
        generatePassword, generatePronounceable, generateFromPattern,
        rollDiceware, diceToIndex,
    };
})(window);
